import {useEffect, useRef, useState} from 'react';
import {getSigningStatus, getSigningEventsUrl} from '../api/renewalClickApi';
import {SIGNING_SSE_EVENT} from '../constants/renewalClick';

export const useSigningStatus = (shortLinkKey, enabled, recoveryTrigger) => {
  const [signingCompleted, setSigningCompleted] = useState(false);
  const [finalizationFailed, setFinalizationFailed] = useState(false);
  const sseRef = useRef(null);
  const checkStatusRef = useRef(null);

  useEffect(() => {
    if (!enabled || !shortLinkKey) return;
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const result = await getSigningStatus(shortLinkKey);
        if (!cancelled && result?.signed) setSigningCompleted(true);
      } catch {
        // Non-critical
      }
    };
    checkStatusRef.current = checkStatus;

    let hasCheckedThisOutage = false;
    try {
      const eventsUrl = getSigningEventsUrl(shortLinkKey);
      const source = new EventSource(eventsUrl, {withCredentials: true});
      sseRef.current = source;

      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.event === SIGNING_SSE_EVENT.COMPLETED) {
            setFinalizationFailed(false);
            setSigningCompleted(true);
          } else if (data.event === SIGNING_SSE_EVENT.FINALIZATION_FAILED) {
            // The stream stays open: a webhook retry can still succeed.
            setFinalizationFailed(true);
          }
        } catch {
          // ignore parse errors
        }
      };

      source.onopen = () => {
        hasCheckedThisOutage = false;
      };

      source.onerror = () => {
        // SSE reconnects automatically; only recover once per disconnect
        // episode so the browser's native retry cycle doesn't turn this
        // into a polling loop.
        if (!hasCheckedThisOutage) {
          hasCheckedThisOutage = true;
          checkStatus();
        }
      };
    } catch {
      // EventSource unavailable — one-shot fallback check
      checkStatus();
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkStatus();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      if (sseRef.current) sseRef.current.close();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, shortLinkKey]);

  // Recovery check on the one signal that means completion is newly possible
  useEffect(() => {
    if (recoveryTrigger) checkStatusRef.current?.();
  }, [recoveryTrigger]);

  useEffect(() => {
    if (signingCompleted) {
      if (sseRef.current) sseRef.current.close();
    }
  }, [signingCompleted]);

  return {signingCompleted, finalizationFailed};
};
